import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, Layers, Loader2, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { authFetch } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { validatePrizeRows, buildPrizesPayload } from '@/lib/main-event-prizes';
import type { PrizeRow } from '@/types/main-event-prize.types';
import { FORMAT_DESCRIPTIONS, STOCK_DESCRIPTIONS } from '@/services/events/main-event/format-descriptions';

// Card leagues draw their weekly slate from the checked-in NFL schedule, so the
// sport is fixed to NFL until another sport ships a season fixture set.
const SPORT = 'NFL';
const POST_GAME_BUFFER_MS = 4 * 60 * 60 * 1000;

const FORMAT_OPTIONS = [
  { value: 'BEST_BALL', label: 'Best Ball (auto-lineup)' },
  { value: 'DRAFT_PPR', label: 'PPR (weekly lineup)' },
];

interface ScheduleWeek {
  round: number;
  week: number;
  seasonType: 'PRESEASON' | 'REGULAR';
  label: string;
  gamesCount: number;
  firstKickoff: string;
  lastKickoff: string;
}

export default function AdminCardLeagueNewPage() {
  const router = useRouter();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [scheduleAvailable, setScheduleAvailable] = useState(false);
  const [weeks, setWeeks] = useState<ScheduleWeek[]>([]);

  const [form, setForm] = useState({
    title: '',
    description: FORMAT_DESCRIPTIONS.BEST_BALL,
    imageUrl: '',
    format: 'BEST_BALL',
    durationClass: 'SEASON' as 'SEASON' | 'MONTHLY',
    rosterSize: 10,
    minRare: 0,
    minLegendary: 0,
    entryFee: 0,
    prizePool: 0,
    highlighted: false,
  });
  // MONTHLY leagues run over a slice of the season; SEASON spans every week.
  const [weekStart, setWeekStart] = useState<number | ''>('');
  const [weekEnd, setWeekEnd] = useState<number | ''>('');

  const [prizes, setPrizes] = useState<PrizeRow[]>([
    { placement: 1, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' },
    { placement: 2, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' },
    { placement: 3, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' },
  ]);

  // Regular-season weeks only — card leagues never open on preseason rounds.
  const regularWeeks = useMemo(() => weeks.filter((w) => w.seasonType === 'REGULAR'), [weeks]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authFetch(`/api/admin/season-schedule?sport=${SPORT}`);
        const data = await res.json();
        const payload = data.data ?? data;
        if (!active) return;
        if (res.ok && payload?.available) {
          const all: ScheduleWeek[] = payload.weeks ?? [];
          setWeeks(all);
          setScheduleAvailable(true);
          const reg = all.filter((w) => w.seasonType === 'REGULAR');
          if (reg.length > 0) {
            // Default a MONTHLY slice to the first four weeks.
            setWeekStart(reg[0].round);
            setWeekEnd(reg[Math.min(3, reg.length - 1)].round);
          }
        } else {
          setScheduleAvailable(false);
        }
      } catch {
        if (active) setScheduleAvailable(false);
      } finally {
        if (active) setLoadingSchedule(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Weeks the league will actually cover, given format/duration selections.
  const selectedWeeks = useMemo(() => {
    if (form.durationClass === 'SEASON') return regularWeeks;
    if (weekStart === '' || weekEnd === '') return [];
    return regularWeeks.filter((w) => w.round >= weekStart && w.round <= weekEnd);
  }, [form.durationClass, regularWeeks, weekStart, weekEnd]);

  // startTime/endTime are derived from the slate — the same anchors the seeds use.
  const derivedWindow = useMemo(() => {
    if (selectedWeeks.length === 0) return null;
    const firsts = selectedWeeks.map((w) => new Date(w.firstKickoff).getTime());
    const lasts = selectedWeeks.map((w) => new Date(w.lastKickoff).getTime());
    return {
      startTime: new Date(Math.min(...firsts)),
      endTime: new Date(Math.max(...lasts) + POST_GAME_BUFFER_MS),
    };
  }, [selectedWeeks]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value,
    }));
  };

  // Swap in the format's stock blurb, but only while the description is empty or
  // still a stock default — never clobber copy the admin has customized.
  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const format = e.target.value;
    setForm((prev) => {
      const replaceable = prev.description.trim() === '' || STOCK_DESCRIPTIONS.has(prev.description);
      return {
        ...prev,
        format,
        description: replaceable ? (FORMAT_DESCRIPTIONS[format] ?? prev.description) : prev.description,
      };
    });
  };

  const handlePrizeChange = (index: number, field: keyof PrizeRow, value: string | number) => {
    setPrizes((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };
  const addPrize = () => {
    const nextPlacement = prizes.length > 0 ? Math.max(...prizes.map((p) => p.placement)) + 1 : 1;
    setPrizes((prev) => [...prev, { placement: nextPlacement, prizeType: 'COINS', coinAmount: 0, itemName: '', itemImageUrl: '' }]);
  };
  const removePrize = (index: number) => setPrizes((prev) => prev.filter((_, i) => i !== index));

  const coinTotal = prizes.filter((p) => p.prizeType === 'COINS').reduce((sum, p) => sum + p.coinAmount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!scheduleAvailable) {
      setError(`No ${SPORT} season schedule is available to build weeks from.`);
      return;
    }
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (form.durationClass === 'MONTHLY' && (weekStart === '' || weekEnd === '' || weekStart > weekEnd)) {
      setError('Pick a valid week range (start week must be on or before end week).');
      return;
    }
    if (!derivedWindow || selectedWeeks.length === 0) {
      setError('The selected duration covers no weeks — check the week range.');
      return;
    }
    if (!Number.isInteger(form.rosterSize) || form.rosterSize <= 0) {
      setError('Roster size must be a positive whole number.');
      return;
    }
    if (form.prizePool > 0 && coinTotal > form.prizePool) {
      setError(`Coin prizes total (${coinTotal.toLocaleString()}) exceeds prize pool (${form.prizePool.toLocaleString()})`);
      return;
    }
    const prizeError = validatePrizeRows(prizes);
    if (prizeError) {
      setError(prizeError);
      return;
    }

    setCreating(true);
    setError('');

    const prizesPayload = buildPrizesPayload(prizes);
    const prizeDistribution: Record<string, number> = {};
    for (const p of prizes) {
      if (p.prizeType === 'COINS' && p.coinAmount > 0) prizeDistribution[String(p.placement)] = p.coinAmount;
    }

    try {
      const res = await authFetch('/api/admin/main-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description || null,
          imageUrl: form.imageUrl || null,
          sport: SPORT,
          eventType: 'BRACKET',
          format: form.format,
          durationClass: form.durationClass,
          accessType: 'OPEN',
          highlighted: form.highlighted,
          formatConfig: {
            rosterSize: form.rosterSize,
            gates: { minRare: form.minRare, minLegendary: form.minLegendary },
          },
          startTime: derivedWindow.startTime.toISOString(),
          endTime: derivedWindow.endTime.toISOString(),
          entryFee: form.entryFee,
          prizePool: form.prizePool,
          prizeDistribution: Object.keys(prizeDistribution).length > 0 ? prizeDistribution : null,
          prizes: prizesPayload,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create league');
        setCreating(false);
        return;
      }

      const eventId = data.data?.id || data.id;

      // Generate the weekly undercards. SEASON → whole regular season (empty
      // body uses the length-derived default); MONTHLY → the chosen slice.
      const generateBody =
        form.durationClass === 'MONTHLY' ? { weekStart, weekEnd } : {};
      try {
        await authFetch(`/api/admin/main-events/${eventId}/undercards/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(generateBody),
        });
      } catch {
        // Non-fatal: the edit page's Generate panel can retry.
      }

      router.push(`/admin/main-events/${eventId}`);
    } catch {
      setError('Failed to create league');
      setCreating(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:border-red-500 focus:outline-none [color-scheme:dark]';
  const smallInputClass =
    'w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-red-500 focus:outline-none';
  const labelClass = 'block text-sm font-medium text-zinc-400 mb-1';

  const weekLabelFor = (round: number | '') =>
    round === '' ? '' : regularWeeks.find((w) => w.round === round)?.label ?? `Week ${round}`;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/admin/main-events"
          className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <Layers className="h-6 w-6 text-red-400 flex-shrink-0" />
        <h1 className="text-xl sm:text-2xl font-bold text-white">New Card League</h1>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-800 bg-red-950/50 p-3 text-sm text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loadingSchedule && !scheduleAvailable && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-800 bg-amber-950/40 p-3 text-sm text-amber-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            No {SPORT} season schedule is checked in, so weeks can&apos;t be generated. Add the fixtures before creating a
            card league.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">League Basics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelClass}>Title *</label>
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder="e.g., NFL Best Ball 2026"
                className={inputClass}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={4}
                placeholder="League description..."
                className={`${inputClass} resize-none`}
              />
              <p className="mt-1 text-xs text-zinc-500">
                Auto-fills from the format&apos;s stock blurb — edit freely; your copy won&apos;t be overwritten.
              </p>
            </div>

            <div>
              <label className={labelClass}>Sport</label>
              <input type="text" value={`Football — ${SPORT}`} disabled className={`${inputClass} opacity-60`} />
            </div>

            <div>
              <label className={labelClass}>Format</label>
              <select name="format" value={form.format} onChange={handleFormatChange} className={inputClass}>
                {FORMAT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 pt-2 md:col-span-2">
              <input
                type="checkbox"
                id="highlighted"
                name="highlighted"
                checked={form.highlighted}
                onChange={(e) => setForm((prev) => ({ ...prev, highlighted: e.target.checked }))}
                className="h-4 w-4 rounded border-zinc-600 bg-zinc-800"
              />
              <label htmlFor="highlighted" className="text-sm text-zinc-400">
                Highlight this league
              </label>
            </div>
          </div>
        </div>

        {/* Schedule + duration */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Schedule</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Duration</label>
              <select name="durationClass" value={form.durationClass} onChange={handleChange} className={inputClass}>
                <option value="SEASON">Season (all weeks)</option>
                <option value="MONTHLY">Monthly (week range)</option>
              </select>
              <p className="mt-1 text-xs text-zinc-500">
                A card locks into one Season and one Monthly league at a time (two-slot rule).
              </p>
            </div>

            {form.durationClass === 'MONTHLY' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Start week</label>
                  <select
                    value={weekStart}
                    onChange={(e) => setWeekStart(e.target.value === '' ? '' : Number(e.target.value))}
                    className={inputClass}
                    disabled={regularWeeks.length === 0}
                  >
                    {regularWeeks.map((w) => (
                      <option key={w.round} value={w.round}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>End week</label>
                  <select
                    value={weekEnd}
                    onChange={(e) => setWeekEnd(e.target.value === '' ? '' : Number(e.target.value))}
                    className={inputClass}
                    disabled={regularWeeks.length === 0}
                  >
                    {regularWeeks.map((w) => (
                      <option key={w.round} value={w.round}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 rounded-lg bg-zinc-800/50 border border-zinc-700 p-3 text-sm text-zinc-300">
            {loadingSchedule ? (
              <span className="flex items-center gap-2 text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading schedule…
              </span>
            ) : selectedWeeks.length > 0 && derivedWindow ? (
              <>
                <span className="font-medium text-white">
                  {selectedWeeks.length} week{selectedWeeks.length === 1 ? '' : 's'}
                </span>{' '}
                {form.durationClass === 'MONTHLY' && (
                  <span className="text-zinc-400">
                    ({weekLabelFor(weekStart)} → {weekLabelFor(weekEnd)}){' '}
                  </span>
                )}
                will be generated · runs {derivedWindow.startTime.toLocaleDateString()} →{' '}
                {derivedWindow.endTime.toLocaleDateString()}
              </>
            ) : (
              <span className="text-zinc-400">No weeks selected.</span>
            )}
          </div>
        </div>

        {/* Roster + gates */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Roster &amp; Entry Gates</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Roster size</label>
              <input
                type="number"
                name="rosterSize"
                min={1}
                value={form.rosterSize}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Min Rare-or-better</label>
              <input
                type="number"
                name="minRare"
                min={0}
                value={form.minRare}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Min Legendary</label>
              <input
                type="number"
                name="minLegendary"
                min={0}
                value={form.minLegendary}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-zinc-500">
            Gates are cumulative on the locked roster (a Legendary counts toward Rare-or-better). Set both to 0 for an
            open league.
          </p>
        </div>

        {/* Entry fee + prizes */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Entry &amp; Prizes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelClass}>Entry Fee (coins)</label>
              <input
                type="number"
                name="entryFee"
                min={0}
                value={form.entryFee}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Prize Pool (coins)</label>
              <input
                type="number"
                name="prizePool"
                min={0}
                value={form.prizePool}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
          </div>

          <div className="space-y-2">
            {prizes.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-16 text-sm text-zinc-400">#{p.placement}</span>
                <select
                  value={p.prizeType}
                  onChange={(e) => handlePrizeChange(i, 'prizeType', e.target.value)}
                  className={smallInputClass}
                >
                  <option value="COINS">Coins</option>
                  <option value="ITEM">Item</option>
                </select>
                {p.prizeType === 'COINS' ? (
                  <input
                    type="number"
                    min={0}
                    value={p.coinAmount}
                    onChange={(e) => handlePrizeChange(i, 'coinAmount', e.target.value === '' ? 0 : Number(e.target.value))}
                    placeholder="Coins"
                    className={smallInputClass}
                  />
                ) : (
                  <input
                    type="text"
                    value={p.itemName}
                    onChange={(e) => handlePrizeChange(i, 'itemName', e.target.value)}
                    placeholder="Item name"
                    className={smallInputClass}
                  />
                )}
                <button
                  type="button"
                  onClick={() => removePrize(i)}
                  className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-zinc-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPrize}
              className="flex items-center gap-1 text-sm text-red-400 hover:text-red-300"
            >
              <Plus className="h-4 w-4" /> Add prize
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Link href="/admin/main-events">
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={creating || loadingSchedule || !scheduleAvailable}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating…
              </>
            ) : (
              'Create League'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
